import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getRMACollection } from "@tKgpco/Services/RMAService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import loadRMACollection from "@tKgpco/Store/Data/RMA/Handlers/loadRMACollection";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    userName: state.context.session.userName,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isLoggedIN: state.context.session.isAuthenticated,
    RMAStore: state.KGPState.RMAStore,
    parameters: state.KGPState.RMAStore.RMACollectionParams,
    location: getLocation(state),
    featurePages: state.context.session.properties.featurePages,
    isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface RMAHistoryStyles {
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
}

export const rmaHistoryStyles: RMAHistoryStyles = {
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
};

const styles = rmaHistoryStyles;

const mapDispatchToProps = {
    loadRMACollection,
};

const RMAHistoryContainer: React.FC<Props> = ({
    id,
    userName,
    signInUrl,
    currentUserIsGuest,
    history,
    RMAStore,
    loadRMACollection,
    parameters,
    location,
    featurePages,
    isSecurityRolesAssigned,
}) => {
    useEffect(() => {
        if (!(userName && !currentUserIsGuest)) {
            history.push(`${signInUrl}`);
        }
    }, [userName, currentUserIsGuest]);

    useEffect(() => {
        if (!RMAStore.isLoading && !RMAStore.RMACollection && userName && !currentUserIsGuest) {
            loadRMACollection({ ...parameters });
        }
    }, [RMAStore, userName, currentUserIsGuest]);

    if (featurePages?.includes("RMA History")) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="no-access-message">
                    {translate(`You don’t have permission to view this page`)}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RMAHistoryContainer)),
    definition: {
        group: "Common",
        displayName: "RMAHistoryContainer",
        fieldDefinitions: [],
    },
};

export default widgetModule;
